import { useRef, useState } from 'react';
import { InputSwitch, globalToastService } from '@repo/atomic-components';
import type { InputSwitchChangeEvent } from '@repo/atomic-components';
import { usePostUserUpdatePreferencesMutation } from '@/api';
import { selectUserPreferences, useAppSelector } from '@/store';
import type { SettingControlProps } from '../types';

/**
 * Saves on toggle. `optimistic` shows the new value until the store updates; a failed save reverts.
 * The switch stays enabled while saving, so `latestRequestRef` lets only the newest request win.
 */
export const EnterToSendControl = ({ labelId }: SettingControlProps) => {
  const [postUpdatePreferences] = usePostUserUpdatePreferencesMutation();
  const userPreferences = useAppSelector(selectUserPreferences);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const latestRequestRef = useRef(0);

  const enterToSend = userPreferences?.enterToSend ?? false;
  const checked = optimistic ?? enterToSend;

  const handleChange = async (event: InputSwitchChangeEvent) => {
    const next = Boolean(event.value);
    const requestId = ++latestRequestRef.current;

    setOptimistic(next);

    try {
      await postUpdatePreferences({ enterToSend: next }).unwrap();
    } catch (error) {
      console.error('Failed to update preferences:', error);

      if (requestId !== latestRequestRef.current) return;

      globalToastService.show({
        closable: true,
        severity: 'error',
        detail: 'Failed to update preferences. Please try again.',
        life: 6000,
      });
    } finally {
      // Hand the switch back to the store, which `onQueryStarted` has already updated on
      // success and left untouched on failure — so this both settles and reverts.
      if (requestId === latestRequestRef.current) setOptimistic(null);
    }
  };

  return <InputSwitch checked={checked} onChange={handleChange} aria-labelledby={labelId} />;
};
